import client from "../client";

export interface AdsOptimizationConfigDto {
  userId: string;
  enabled: boolean;
  killSwitch: boolean;
  dryRunUntil?: string | null;
  dailyBudgetCap: number;
  notOnOtas: boolean;
  bpComplaints: boolean;
  allowBpDominance: boolean;
  excludeFromReallocation: boolean;
}

export interface AdsOptimizationConfigPatch {
  enabled?: boolean;
  killSwitch?: boolean;
  startDryRunDays?: number;
  /** ISO date string, or null to clear (used by "Switch to live"). */
  dryRunUntil?: string | null;
  dailyBudgetCap?: number;
  notOnOtas?: boolean;
  bpComplaints?: boolean;
  allowBpDominance?: boolean;
  excludeFromReallocation?: boolean;
}

export type AdsOptimizationConfigChangeAction =
  | "start_dry_run"
  | "switch_to_live"
  | "pause"
  | "resume"
  | "turn_off"
  | "enable"
  | "update_settings";

export interface AdsOptimizationConfigChangeDto {
  id: string;
  createdAt: string;
  userId: string;
  changedByUserId?: string;
  changedByEmail?: string;
  action: AdsOptimizationConfigChangeAction;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
}

export type AdsOptimizationRuleCode =
  | "A"
  | "B"
  | "B_FIRST"
  | "C"
  | "D"
  | "D_ESCALATE";

export type AdsOptimizationAction =
  | "budget_decrease"
  | "budget_increase"
  | "budget_park"
  | "budget_reallocate"
  | "bid_decrease"
  | "keyword_pause";

export type AdsOptimizationMode = "live" | "dry_run";

export type AdsOptimizationMutationStatus =
  | "applied"
  | "skipped_dry_run"
  | "skipped_kill_switch"
  | "skipped_guardrail"
  | "skipped_mcc_readonly"
  | "failed";

export interface AdsOptimizationDecisionDto {
  id: string;
  createdAt: string;
  runId: string;
  ruleCode: AdsOptimizationRuleCode;
  entityType: "campaign" | "keyword";
  mccKey: string;
  customerId: string;
  campaignId: string;
  campaignName?: string;
  keywordId?: string;
  keywordText?: string;
  diagnosis: string;
  action: AdsOptimizationAction;
  previousValue: number;
  newValue: number;
  mode: AdsOptimizationMode;
  mutationStatus: AdsOptimizationMutationStatus;
  mutationError?: string | null;
}

export interface AdsOptimizationRunResult {
  userId: string;
  runId: string;
  status: "completed" | "skipped" | "failed";
  reason?: string;
  decisionsCount: number;
  mode: AdsOptimizationMode | null;
}

export function fetchAdsOptimizationConfig(
  userId: string,
): Promise<AdsOptimizationConfigDto> {
  return client
    .get(`/admin/ads-optimization/config/${userId}`)
    .then((r) => r.data);
}

export function updateAdsOptimizationConfig(
  userId: string,
  patch: AdsOptimizationConfigPatch,
): Promise<AdsOptimizationConfigDto> {
  return client
    .patch(`/admin/ads-optimization/config/${userId}`, patch)
    .then((r) => r.data);
}

export function runAdsOptimizationNow(
  userId: string,
): Promise<AdsOptimizationRunResult> {
  return client
    .post(`/admin/ads-optimization/run/${userId}`)
    .then((r) => r.data);
}

export function fetchAdsOptimizationDecisions(
  userId: string,
  opts: { limit?: number; before?: string } = {},
): Promise<AdsOptimizationDecisionDto[]> {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.before) params.set("before", opts.before);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return client
    .get(`/admin/ads-optimization/decisions/${userId}${qs}`)
    .then((r) => r.data);
}

export function fetchAdsOptimizationConfigChanges(
  userId: string,
  opts: { limit?: number } = {},
): Promise<AdsOptimizationConfigChangeDto[]> {
  const qs = opts.limit ? `?limit=${opts.limit}` : "";
  return client
    .get(`/admin/ads-optimization/config-changes/${userId}${qs}`)
    .then((r) => r.data);
}
